import React from 'react';
import { StatusBadge } from '@/components/ui';
import type { ResourceRate } from '../../../services/vendorService';
import { currency } from '../utils/format';
import { summariseRatesByArea, upcomingLabel } from '../utils/rateDisplay';

/**
 * M19 - the Rates column on resource lists: the rate in effect TODAY per service area, with
 * any approved future rate labelled ("$175.00 from Jan 1, 2027") rather than shown as if it
 * were already current. Pending changes and first rates are a count, not a price.
 */
const ResourceRatesCell: React.FC<{ rates?: ResourceRate[] | null }> = ({ rates }) => {
  const summary = summariseRatesByArea(rates ?? []);
  const pending = summary.reduce((n, s) => n + s.pending.length, 0);
  const priced = summary.filter((s) => s.current || s.upcoming.length);

  return (
    <span className="tw-inline-flex tw-flex-col tw-gap-0.5">
      {priced.length === 0 && <span className="tw-text-content-muted">No current rate</span>}
      {priced.map((s) => (
        <span key={s.serviceAreaId} className="tw-whitespace-nowrap">
          {summary.length > 1 && <span className="tw-text-content-muted">{s.areaLabel}: </span>}
          {s.current ? currency(s.current.rate) : <span className="tw-text-content-muted">No current rate</span>}
          {s.upcoming.map((u) => (
            <span key={u.id} className="tw-ml-2 tw-text-sm tw-text-content-secondary">
              · {upcomingLabel(u)}
            </span>
          ))}
        </span>
      ))}
      {pending > 0 && (
        <span>
          {/* V3-035: the tint style of "Awaiting approval", not a solid amber badge. */}
          <StatusBadge status="pending" label={`${pending} pending`} variant="warning" />
        </span>
      )}
    </span>
  );
};

export default ResourceRatesCell;
